import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePassport } from '../../../../hooks/usePassport';
import { useState } from 'react';

import { 
  User,          // 기본 정보 (아이디)
  PlusCircle,    // 추가 버튼 (부모/자녀 추가)
  Camera
} from 'lucide-react';

const ChildrenSection = () => {
  const { passport } = usePassport();

	// 1. 자녀 영역 임시 데이터 (기존 코드 디자인 확인용)
  const [children, setChildren] = useState([
    { name: "김태양", birth: "20150512", gender: "M" },
    { name: "김바다", birth: "20181120", gender: "F" }
  ]);
  const [editChildIndex, setEditChildIndex] = useState(null);

  // 2. 핸들러 (UI 동작 확인용)
  const handleAddChild = () => {
    setChildren([...children, { name: "", birth: "", gender: "" }]);
    setEditChildIndex(children.length); // 추가 즉시 수정모드
  };

  const handleChildDelete = (index) => {
    setChildren(children.filter((_, i) => i !== index));
  };

  const handleChildChange = (index, field, value) => {
    const newChildren = [...children];
    newChildren[index][field] = value;
    setChildren(newChildren);
  };

  const handleChildComplete = (index) => {
    setEditChildIndex(null);
  };


	return (
		<section className="space-y-3">
			<div className="flex flex-wrap gap-2 items-baseline">
				<h2 className="text-m text-gray-500 font-bold tracking-tighter">자녀</h2>
				<span className="text-xs text-slate-400 font-medium">자녀는 최대 4명까지 등록 가능합니다.</span>
			</div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {children.map((child, index) => (
      <div key={index} className="w-full">
        {editChildIndex !== index ? (
          /* [보기 모드] 왼쪽 카드 - 점선 - 오른쪽 여권 */
          <div className="flex flex-col md:flex-row items-center justify-between gap-0">
            
            {/* 1. 왼쪽: 자녀 정보 카드 (기존 디자인 유지) */}
            <div 
              onClick={() => setEditChildIndex(index)}
              className="relative p-5 border rounded-xl bg-gray-50 flex flex-col cursor-pointer hover:border-purple-400 transition-colors group w-full md:w-[50%] min-h-[260px] items-end "
            >
            	<div className="flex space-x-3 mb-1">
								<button 
									className="text-xs text-blue-500 hover:text-blue-700 font-medium"
									onClick={(e) => {
										e.stopPropagation();
										handleParentEdit(index);
									}}
								>
									수정
								</button>
								<button 
									className="text-xs text-red-500 hover:text-red-700 font-medium"
									onClick={(e) => {
										e.stopPropagation();
										handleParentDelete(index);
									}}
								>
									삭제
								</button>
							</div>

              <div className="flex flex-col justify-center w-full text-left pb-3 border-b">
                <div className="flex-shrink-0 w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm mb-3">
                  <User className="w-6 h-6 text-gray-400" />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-[15px] font-bold text-gray-900 truncate">
                    {child.name || "이름 없음"} ({child.gender === "M" ? "남" : child.gender === "F" ? "여" : "-"})
                  </p>
                  <div className="mt-1 space-y-0.5">
                    <p className="text-xs text-gray-500 flex items-center">
                      <span className="w-16 text-gray-400 tracking-tighter flex-shrink-0">생년월일</span>
                      <span>{child.birth || "-"}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>


            {/* 3. 오른쪽: 여권 정보 디자인 카드 */}
            
                <div className="group relative overflow-hidden rounded-[12px] border-2 border-slate-200 hover:border-blue-400 transition-all shadow-sm hover:shadow-md min-h-[260px] md:w-[48%] bg-white flex flex-col">
  
  {/* 1. 여권 상단 장식 (Header) */}
  <div className="bg-blue-900 h-10 flex items-center px-4 justify-between shrink-0">
    <span className="text-[10px] text-blue-200 tracking-[0.2em] font-bold uppercase">Republic of Korea</span>
    <div className="flex gap-1">
      <div className="w-1.5 h-1.5 rounded-full bg-blue-400 opacity-50" />
      <div className="w-1.5 h-1.5 rounded-full bg-blue-400 opacity-50" />
    </div>
  </div>

  {/* 2. 여권 메인 정보 (Content) */}
  <div className="p-5 flex-1 flex flex-col justify-center">
    <div className="space-y-4">
      
      {/* 성명 섹션 (Full Width) */}
      <div className="border-b border-gray-50 pb-1">
        <p className="text-[9px] text-gray-400 font-bold uppercase leading-none mb-1">Surname / Given Name</p>
        <p className="font-bold text-lg text-slate-800">김자녀</p>
      </div>

      {/* 상세 정보 섹션 (Grid Layout) */}
      <div className="grid grid-cols-2 gap-y-3 gap-x-4">
        <div>
          <p className="text-[9px] text-gray-400 font-bold uppercase leading-none mb-1">Passport No.</p>
          <p className="font-mono text-[13px] font-bold text-slate-700 tracking-tight">M1234567</p>
        </div>
        <div>
          <p className="text-[9px] text-gray-400 font-bold uppercase leading-none mb-1">Grade</p>
          <p className="font-bold text-[13px] text-slate-700">1학년</p>
        </div>
        <div>
          <p className="text-[9px] text-gray-400 font-bold uppercase leading-none mb-1">Level</p>
          <div className="flex items-center gap-1">
            <span className="text-[13px] font-black text-blue-600">LV.</span>
            <span className="font-mono text-[13px] font-bold text-slate-700">3</span>
          </div>
        </div>
        {/* 우측 하단 빈 공간은 상태 배지 등으로 활용 가능 */}
        <div className="flex items-end justify-end">
           <span className="text-[8px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-500 border border-blue-100 font-bold">VALID</span>
        </div>
      </div>
    </div>
  </div>

  {/* 3. 여권 하단 영역 (MRZ - Machine Readable Zone) */}
  <div className="bg-gray-50 p-2.5 border-t border-gray-100 text-[10px] font-mono text-gray-400 overflow-hidden whitespace-nowrap tracking-tighter shrink-0">
    P&lt;KORNAME&lt;&lt;CHILD&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;
  </div>
</div>
          </div>
        ) : (
          /* [수정 모드] 기존 입력폼 디자인 유지 */
          <div className="p-6 border-2 border-purple-500 rounded-2xl bg-white space-y-4 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="자녀 이름을 입력해주세요."
                value={child.name}
                onChange={(e) => handleChildChange(index, 'name', e.target.value)}
                className="w-full px-4 py-3 rounded-lg border text-sm"
              />
              <input
                type="text"
                placeholder="생년월일(8자리)을 입력해주세요."
                value={child.birth}
                onChange={(e) => handleChildChange(index, 'birth', e.target.value)}
                className="w-full px-4 py-3 rounded-lg border text-sm"
              />
            </div>
            <div className="flex w-full rounded-xl border overflow-hidden">
              <label className={`flex-1 text-center py-3 cursor-pointer transition text-sm ${child.gender === "M" ? "bg-purple-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
                <input type="radio" name={`gender-${index}`} value="M" checked={child.gender === "M"} onChange={(e) => handleChildChange(index, 'gender', e.target.value)} className="hidden" />
                남자
              </label>
              <label className={`flex-1 text-center py-3 cursor-pointer transition text-sm border-l ${child.gender === "F" ? "bg-purple-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
                <input type="radio" name={`gender-${index}`} value="F" checked={child.gender === "F"} onChange={(e) => handleChildChange(index, 'gender', e.target.value)} className="hidden" />
                여자
              </label>
            </div>
            <button
              type="button"
              onClick={() => handleChildComplete(index)}
              className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700"
            >
              입력 완료
            </button>
          </div>
        )}
      </div>
    ))}
<button
      type="button"
      onClick={handleAddChild}
      className="w-full py-4 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center gap-2 text-gray-400 hover:bg-gray-50 hover:border-gray-300 transition-colors group"
    >
      <PlusCircle className="w-5 h-5 " />
    </button>
  </div>
          </section>
	);
}

export default ChildrenSection;